import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';

import { of } from 'rxjs';
import { switchMap, map, catchError, tap, filter } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from 'src/app/core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { CustomRouterNavigatedAction } from 'src/app/core/custom-route-serializer';
import { STUDIES_ROUTES } from '../../studies/studies.routes';
import { TemplatesService } from '../../services/templates/templates.service';
import * as templateActions from './templates.actions';
import * as sectionActions from './../sections/sections.actions';
import { Template } from 'src/app/models/template';
import { transformLanguageCodesToLanguageEntities } from 'src/app/core/languages-helper';

export function transformTemplateFromApi(template): Template {
  const languagesArray = template.languages.split(',');
  const newTranslations = [];
  for (const translation of template.translations) {
    if (languagesArray.find((langCode) => langCode === translation.language)) {
      newTranslations.push(translation);
    }
  }
  return {
    ...template,
    languages: languagesArray,
    translations: newTranslations,
  };
}

@Injectable()
export class TemplateEffects {
  templatesRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return route.payload.routerState.url.endsWith(`/${STUDIES_ROUTES.TEMPLATES}`);
      }),
      map((action) =>
        templateActions.getTemplates({
          studyId: action.payload.routerState.params.id,
        })
      )
    )
  );

  getTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.getTemplates),
      switchMap(({ studyId }) => {
        return this.templatesService.getTemplates(studyId).pipe(
          map((templates) => {
            const transformedTemplates = [];
            for (const template of templates) {
              const transformedTemplate: Template = this.dashboardCamelCasePipe.keysToCamelCase(template);
              transformedTemplates.push(transformedTemplate);
            }
            return templateActions.getTemplatesSuccess({
              templates: transformedTemplates,
            });
          }),
          catchError((err: HttpErrorResponse) => of(templateActions.getTemplatesError({ err })))
        );
      })
    )
  );

  getTemplatesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.getTemplatesError),
        tap(({ err }) => this.toastrService.error('Unable to load templates'))
      ),
    { dispatch: false }
  );

  getTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.getTemplate),
      switchMap(({ templateId }) => {
        return this.templatesService.getTemplateById(templateId).pipe(
          map((selectedTemplate) => {
            const templateCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(selectedTemplate);
            return templateActions.getTemplateSuccess({
              selectedTemplate: transformTemplateFromApi(templateCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => of(templateActions.getTemplateError({ err })))
        );
      })
    )
  );

  getTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.getTemplateSuccess),
      map(({ selectedTemplate }) => {
        const transformedLanguages = transformLanguageCodesToLanguageEntities(selectedTemplate.languages);
        return templateActions.addTemplateSupportedLanguages({
          supportedLanguages: transformedLanguages,
        });
      })
    )
  );

  getTemplateError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.getTemplateError),
        tap(({ err }) => this.toastrService.error('Unable to load template'))
      ),
    { dispatch: false }
  );

  createTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.createTemplate),
      switchMap(({ template }) => {
        const templateSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(template);
        return this.templatesService.createTemplate(templateSnakeCase).pipe(
          map((createdTemplate: Template) => {
            template = {
              ...template,
              id: createdTemplate.id,
              hash: createdTemplate.hash,
            };
            const createdTemplateCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(template);
            return templateActions.createTemplateSuccess({
              createdTemplate: transformTemplateFromApi(createdTemplateCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(templateActions.createTemplateError({ err }));
          })
        );
      })
    )
  );

  createTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.createTemplateSuccess),
      map(({ createdTemplate }) => {
        this.toastrService.success(`Template ${createdTemplate.templateName} created!`);
        return sectionActions.getSections({
          templateId: createdTemplate.id,
        });
      })
    )
  );

  createTemplateError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.createTemplateError),
        tap(({ err }) => {
          this.toastrService.error('Unable to create new template');
        })
      ),
    { dispatch: false }
  );

  updateTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.updateTemplate),
      switchMap(({ template }) => {
        const templateSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(template);
        return this.templatesService.updateTemplate(templateSnakeCase).pipe(
          map((updatedTemplate: Template) => {
            const updatedTemplateCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(template);
            return templateActions.updateTemplateSuccess({
              updatedTemplate: transformTemplateFromApi(updatedTemplateCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(templateActions.updateTemplateError({ err }));
          })
        );
      })
    )
  );

  updateTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.updateTemplateSuccess),
        tap(({ updatedTemplate }) => {
          this.toastrService.success(`Template ${updatedTemplate.templateName} updated!`);
        })
      ),
    { dispatch: false }
  );

  updateTemplateError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.updateTemplateError),
        tap(({ err }) => {
          this.toastrService.error('Unable to update template');
        })
      ),
    { dispatch: false }
  );

  deleteTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.deleteTemplate),
      switchMap(({ template }) => {
        const templateSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(template);
        return this.templatesService.deleteTemplate(templateSnakeCase).pipe(
          map(() => templateActions.deleteTemplateSuccess({ deletedTemplate: template })),
          catchError((err: HttpErrorResponse) => of(templateActions.deleteTemplateError({ err })))
        );
      })
    )
  );

  deleteTemplatesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.deleteTemplateSuccess),
        tap(({ deletedTemplate }) => {
          this.toastrService.success('Template deleted!');
        })
      ),
    { dispatch: false }
  );

  deleteTemplatesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.deleteTemplateError),
        tap(({ err }) => this.toastrService.error('Unable to delete template'))
      ),
    { dispatch: false }
  );

  importTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.importTemplate),
      switchMap(({ studyId, importFile }) => {
        return this.templatesService.importTemplate(studyId, importFile).pipe(
          map((importedTemplate) => {
            const transformedImportedTemplate: Template = this.dashboardCamelCasePipe.keysToCamelCase(importedTemplate);
            return templateActions.importTemplateSuccess({ importedTemplate: transformedImportedTemplate });
          }),
          catchError((err: HttpErrorResponse) => of(templateActions.importTemplateError({ err })))
        );
      })
    )
  );

  importTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.importTemplateSuccess),
        tap(({ importedTemplate }) => {
          let message = `${importedTemplate.templateName} successfully imported!`;
          if (!importedTemplate.interventionId) {
            message = message.concat(
              ' Please connect template with an intervention in order to continue working on it!'
            );
          }
          this.toastrService.success(message);
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  importTemplateError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.importTemplateError),
        tap(({ err }) => this.toastrService.error(`${err.error.error}`, 'Unable to import this template.'))
      ),
    { dispatch: false }
  );

  exportTemplate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.exportTemplate),
        switchMap(({ template }) => {
          return this.templatesService.exportTemplate(template.id).pipe(
            tap((response: any) => {
              const anchor = document.createElement('a');
              anchor.download = `Export-${template.templateName}.json`;
              anchor.href = window.URL.createObjectURL(new Blob([response], { type: 'text/json' }));
              document.body.appendChild(anchor);
              anchor.click();
              document.body.removeChild(anchor);
            })
          );
        })
      ),
    { dispatch: false }
  );

  setTemplateAsReady$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.setTemplateAsReady),
      switchMap(({ template }) => {
        const templateSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(template);
        return this.templatesService.updateTemplate(templateSnakeCase).pipe(
          map(() => {
            const updatedTemplateCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(template);
            return templateActions.setTemplateAsReadySuccess({
              updatedTemplate: transformTemplateFromApi(updatedTemplateCamelCase),
            });
          }),
          catchError((err: HttpErrorResponse) => {
            return of(templateActions.setTemplateAsReadyError({ err }));
          })
        );
      })
    )
  );

  setTemplateAsReadySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(templateActions.setTemplateAsReadySuccess),
      map(({ updatedTemplate }) => {
        if (updatedTemplate.feedbackTemplateReady) {
          this.toastrService.success('Template is ready for sending!');
        } else {
          this.toastrService.success('Template readiness successfully changed');
        }
        return templateActions.setSelectedTemplate({ selectedTemplate: updatedTemplate });
      })
    )
  );

  setTemplateAsReadyError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(templateActions.setTemplateAsReadyError),
        tap(({ err }) => {
          this.toastrService.error('Unable to set template readiness');
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private templatesService: TemplatesService,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe,
    private dialog: MatDialog
  ) {}
}
